import { useEditorStore } from '@/stores/editor.store';

/**
 */
class ServiceWorkerRegistrationService {
  private registration: ServiceWorkerRegistration | null = null;

  /**
   */
  async register(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      console.warn('⚠️ This browser does not support Service Workers.');
      return;
    }

    if (import.meta.env.DEV) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
      useEditorStore.getState().setServiceWorkerReady(false);
      useEditorStore.getState().setBackgroundSyncSupported(false);
      console.log('🛑 Service Worker is not registered in development mode.');
      return;
    }

    try {
      console.log('🔧 Registering Service Worker...');

      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
      });

      console.log('✅ Service Worker registered');

      useEditorStore.getState().setServiceWorkerReady(true);

      this.checkBackgroundSyncSupport();

      this.registration.addEventListener('updatefound', () => {
        console.log('🔄 Service Worker update found');
      });

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('🔄 Service Worker controller changed');
      });
    } catch (error) {
      console.error('❌ Service Worker registration failed:', error);
      useEditorStore.getState().setServiceWorkerReady(false);
    }
  }

  /**
   */
  private checkBackgroundSyncSupport(): void {
    if ('sync' in (this.registration || {})) {
      console.log('✅ Background Sync is supported');
      useEditorStore.getState().setBackgroundSyncSupported(true);
    } else {
      console.warn('⚠️ Background Sync is not supported. Falling back to regular network requests.');
      useEditorStore.getState().setBackgroundSyncSupported(false);
    }
  }

  /**
   */
  async registerSync(): Promise<void> {
    if (!this.registration) {
      console.warn('⚠️ Service Worker is not registered.');
      return;
    }

    const syncRegistration = this.registration as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    };

    if (!syncRegistration.sync) {
      console.warn('⚠️ Background Sync is not available.');
      return;
    }

    try {
      await syncRegistration.sync.register('save-queue-sync');
      console.log('🔄 Background Sync registered');
    } catch (error) {
      console.error('Background Sync registration failed:', error);
    }
  }

  /**
   */
  async checkForUpdates(): Promise<void> {
    if (!this.registration) {
      console.warn('⚠️ Service Worker is not registered.');
      return;
    }

    try {
      await this.registration.update();
      console.log('🔄 Service Worker update check completed');
    } catch (error) {
      console.error('Service Worker update check failed:', error);
    }
  }

  /**
   */
  async unregister(): Promise<void> {
    if (!this.registration) {
      return;
    }

    try {
      await this.registration.unregister();
      console.log('🛑 Service Worker unregistered');
      useEditorStore.getState().setServiceWorkerReady(false);
      useEditorStore.getState().setBackgroundSyncSupported(false);
    } catch (error) {
      console.error('Service Worker unregister failed:', error);
    }
  }

  /**
   */
  isReady(): boolean {
    return !!this.registration && !!navigator.serviceWorker.controller;
  }
}

export const serviceWorkerRegistration = new ServiceWorkerRegistrationService();
